import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Transition } from '@headlessui/react';
import useTranslation from 'next-translate/useTranslation';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import axios from 'axios';
import classnames from 'classnames';

import {
  getOS,
  copyTextToClipboard,
  getElementTextContent,
  calcDiversityText,
} from '../../utils';

import ApproachContext from '../../context/ApproachContext';
import PublicConfigContext from '../../context/PublicConfigContext';
import { FunnelStore } from '../../context/FunnelContext';

// Components
import AboutCompanySection from './components/aboutCompanySection';
import BenefitsSection from './components/benefitsSection';
import CompanyLogo from './components/companyLogo';
import Footer from './components/footer';
import JobDiversity from './components/jobBulletpoints/jobDiversity';
import JobIntroduction from './components/jobIntroduction';
import JobLocations from './components/jobBulletpoints/jobLocations';
import JobRatings from './components/jobBulletpoints/jobRatings';
import JobRemoteOption from './components/jobBulletpoints/jobRemoteOption';
import JobTitleAndDiversity from './components/jobTitleAndDiversity';
import JobWorkload from './components/jobBulletpoints/jobWorkload';
import NiceToHaveSection from './components/niceToHaveSection';
import NotForYouSection from './components/notForYouSection';
import NotInterestedSignUpSection from './components/notInterestedSignUpSection';
import ReadyStepSection from './components/readyStepSection';
import StepsSection from './components/stepsSection';
import SummarySection from './components/summarySection';
import VideoSection from './components/videoSection';
import WhatYouBringSection from './components/whatYouBringSection';
import WhatYouWillDoSection from './components/whatYouWillDoSection';
import YourTeamSection from './components/yourTeamSection';
import GdprMessage from './components/gdprMessage';

import Button from '../shared/Button';

// Icons
import { LionstepLogo, ShareAndroid, ShareIos } from '../icons';

// Styles
import styles from './jobDescription.module.scss';
import {
  useLocalAnonymousId,
  useSessionAnonymousId,
} from '../../hooks/useAnonymousId';
import { getExperiments } from '../../utils/googleOptimize';

const Funnel = dynamic(() => import('./funnel/Funnel'), { ssr: false });
const Notification = dynamic(() => import('../shared/Notification'));
const ContactFormModal = dynamic(
  () => import('./contact_form_modal/ContactFormModal'),
  { ssr: false },
);

const cx = classnames.bind(styles);

const JobDescription = ({
  job,
  company,
  slug = '',
  uid = '',
  salaryCheck = '',
  lang,
}) => {
  const router = useRouter();
  const { source, utm_source, utm_campaign, utm_medium, title } = router.query;

  const jobTitle = title
    ? decodeURIComponent(title.replaceAll('_', ' '))
    : job.opening_title;

  const publicConfig = useContext(PublicConfigContext);

  const { t } = useTranslation('common');

  const lastScrollPosition = useRef(0);
  const [showFixedBottomCTA, setShowFixedBottomCTA] = useState(false);
  const [showFunnelModal, setShowFunnelModal] = useState(false);
  const [showContactFormModal, setShowContactFormModal] = useState(false);

  const openFunnel = () => {
    setShowFunnelModal(true);
    setShowFixedBottomCTA(false);
  };

  const openContactFormModal = () => {
    setShowContactFormModal(true);
    setShowFixedBottomCTA(false);
  };

  const auid = useLocalAnonymousId();
  const suid = useSessionAnonymousId();

  useEffect(() => {
    if (uid && salaryCheck === 'true') {
      openFunnel();
    }
  }, [uid, salaryCheck]);

  const [showImageCarousel, setShowImageCarousel] = useState(false);

  const [talentId, setTalentId] = useState(uid);
  useEffect(() => {
    if (router.isReady) setTalentId(uid);
  }, [uid, router.isReady]);

  const [shareIcon, setShareIcon] = useState('android');
  useEffect(() => {
    if (getOS() === 'iOS' || getOS() === 'Mac OS') setShareIcon('ios');
  }, []);

  // segment analytics
  useEffect(() => {
    // window.location === window.parent.location --> to prevent runing Segment call in iframe in MainApp
    if (
      router.isReady &&
      window.location === window.parent.location &&
      auid &&
      suid
    ) {
      setTimeout(() => {
        if (job.status !== 'job_closed') {
          const experiments = Object.entries(getExperiments()).join(',');
          axios.get(`${publicConfig.PYTHON_API}/api/v1/jobs/${slug}/viewed`, {
            params: {
              uid,
              auid,
              suid,
              source,
              utm_source,
              utm_campaign,
              utm_medium,
              exps: experiments || undefined,
            },
          });
        }
      }, 1000);
    }
  }, [job.status, uid, auid, suid, router.isReady]);

  // Share Job
  const [showCopiedToClipboard, setShowCopiedToClipboard] = useState(false);
  const isSharingInProgress = useRef(false);

  const shareJob = () => {
    // Prevent multiple simultaneous share calls
    if (isSharingInProgress.current) {
      return;
    }

    const urlWithoutQueryParams = window.location.href.split('?')[0];
    const shareJobQueryParams = new URLSearchParams({
      utm_source: 'multi-channel',
      utm_medium: 'directapplication',
      utm_campaign: 'share_button',
    });

    const urlWithUtmParams = `${urlWithoutQueryParams}?${shareJobQueryParams.toString()}`;

    if (navigator.share) {
      isSharingInProgress.current = true;
      navigator
        .share({
          title: `${jobTitle}
                ${
                  job.extra_info?.diversity
                    ? ` ${calcDiversityText(job.job_language?.mid)}`
                    : ''
                }`,
          text: getElementTextContent(job.extra_info?.introduction),
          url: urlWithUtmParams,
        })
        .then(() => {
          // Share was successful
          isSharingInProgress.current = false;
        })
        .catch(() => {
          // Handle share cancellation or errors
          isSharingInProgress.current = false;
          // Silently handle errors (user cancellation is expected behavior)
        });
    } else {
      copyTextToClipboard(urlWithUtmParams);
      setShowCopiedToClipboard(true);
      setTimeout(() => {
        setShowCopiedToClipboard(false);
      }, 3000);
    }
  };

  useEffect(() => {
    const ctaWrapper = document.getElementById('cta-button-wrapper');
    document.addEventListener(
      'scroll',
      () => {
        const currentScrollPosition = window?.scrollY;

        if (
          ctaWrapper.getBoundingClientRect().top <= 0 &&
          currentScrollPosition < lastScrollPosition.current
        ) {
          setShowFixedBottomCTA(true);
        } else {
          setShowFixedBottomCTA(false);
        }
        lastScrollPosition.current = currentScrollPosition;
      },
      false,
    );
  }, []);

  const COMPANY_COLOR = company.colour ?? '#349CBC';
  const TEXT_COLOR = company.text_colour ?? '#fff';

  const approach = useContext(ApproachContext);
  const isJobClosed = job.status === 'job_closed';

  const ctaButtonsDisabled = isJobClosed;

  const handleOnNextStepsClick = () => {
    if (!ctaButtonsDisabled) {
      openFunnel();
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div
          id="sticky-hero-wrapper"
          className={styles.heroWrapper}
          style={{
            backgroundColor: COMPANY_COLOR,
            color: TEXT_COLOR,
          }}
        >
          <div id="hero-container" className={styles.hero}>
            <div className={styles.heroBranding}>
              <span className={styles.heroLogoWrapper}>
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a
                  href={
                    lang === 'de'
                      ? 'https://www.lionstep.com/de/kandidaten/'
                      : 'https://www.lionstep.com/candidates/'
                  }
                  rel="noopener"
                  target="_blank"
                  aria-label="Lionstep candidate page"
                  data-testid="logo-link-header"
                >
                  <LionstepLogo fill={TEXT_COLOR} className={styles.heroLogo} />
                </a>
              </span>
              {job.extra_info?.can_be_disclosed && (
                <>
                  <p
                    className={styles.recruitingFor}
                    data-testid="recruiting-for"
                  >
                    {t(
                      `description_page.main_section.recruiting_for.${approach}`,
                    )}{' '}
                    {company.company_name}
                  </p>
                </>
              )}
            </div>

            <div className={styles.heroContent}>
              {/* COMPANY LOGO */}
              <CompanyLogo job={job} company={company} />

              {/* JOB TITLE AND DIVERSITY */}
              <JobTitleAndDiversity job={job} title={jobTitle} />

              {/* LOCATIONS AND RATINGS */}
              <div className={styles.bulletPointWrapper}>
                <div className={styles.bulletPoints}>
                  {/* LOCATIONS */}
                  <JobLocations job={job} textColor={TEXT_COLOR} />

                  {/* WORKLOAD PERCENTAGE */}
                  <JobWorkload job={job} textColor={TEXT_COLOR} />

                  {/* REMOTE OPTIONS */}
                  <JobRemoteOption job={job} textColor={TEXT_COLOR} />

                  {/* DIVERSITY */}
                  <JobDiversity company={company} textColor={TEXT_COLOR} />

                  {/* JOB RATINGS */}
                  <JobRatings company={company} textColor={TEXT_COLOR} />
                </div>
              </div>

              {/* INTRODUCTION */}
              <JobIntroduction job={job} />

              {/* INTERESTED BUTTONS */}
              <div className={styles.heroButtons} id="cta-buttons">
                <div
                  className={styles.interestedButtonHero}
                  id="cta-button-wrapper"
                  data-tooltip-id="cta-disabled"
                >
                  <Button
                    data-testid="interested-btn-main"
                    className="interested-btn"
                    style={{
                      color: TEXT_COLOR === '#fff' ? COMPANY_COLOR : TEXT_COLOR,
                    }}
                    type="default"
                    disabled={ctaButtonsDisabled}
                    onClick={openFunnel}
                  >
                    {t(
                      `description_page.main_section.cta_btn_interested.${approach}`,
                    )}
                  </Button>
                </div>
                <div
                  className={styles.shareButtonDesktop}
                  data-tooltip-id="cta-disabled"
                >
                  <Button
                    type="transparent"
                    style={{
                      color: `${TEXT_COLOR}`,
                      border:
                        TEXT_COLOR !== '#fff'
                          ? `1px solid ${TEXT_COLOR}`
                          : '1px solid #d2d6dc',
                    }}
                    disabled={isJobClosed}
                    aria-label={t(
                      `description_page.main_section.cta_btn_share.${approach}`,
                    )}
                    onClick={shareJob}
                  >
                    {shareIcon === 'android' ? (
                      <span
                        className={styles.shareIconDesktop}
                        aria-hidden="true"
                      >
                        <ShareAndroid fill={TEXT_COLOR} />
                      </span>
                    ) : (
                      <span
                        className={styles.shareIconDesktop}
                        aria-hidden="true"
                      >
                        <ShareIos fill={TEXT_COLOR} />
                      </span>
                    )}
                    {t(
                      `description_page.main_section.cta_btn_share.${approach}`,
                    )}
                  </Button>
                </div>
              </div>
              <GdprMessage className={styles.gdpr} />
            </div>
          </div>
          {/* MOBILE & TABLET SHARE BUTTON */}
          <div
            className={styles.shareButtonMobile}
            data-tooltip-id="cta-disabled"
            data-place="bottom"
          >
            <Button
              type="transparent"
              style={{
                color: TEXT_COLOR,
                border:
                  TEXT_COLOR !== '#fff'
                    ? `1px solid ${TEXT_COLOR}`
                    : '1px solid #d2d6dc',
              }}
              disabled={isJobClosed}
              aria-label={t(
                `description_page.main_section.cta_btn_share.${approach}`,
              )}
              onClick={shareJob}
            >
              {shareIcon === 'android' ? (
                <span className={styles.shareIconMobile} aria-hidden="true">
                  <ShareAndroid fill={TEXT_COLOR} />
                </span>
              ) : (
                <span className={styles.shareIconMobile} aria-hidden="true">
                  <ShareIos fill={TEXT_COLOR} />
                </span>
              )}
              <span className={styles.shareTextTablet}>
                {t(`description_page.main_section.cta_btn_share.${approach}`)}
              </span>
            </Button>
          </div>
        </div>

        <div id="description-section" className={styles.sectionsWrapper}>
          <div className={styles.sections}>
            <SummarySection job={job} company={company} />

            {!job.is_one_landing_page && <VideoSection job={job} />}

            <WhatYouWillDoSection job={job} />

            {!job.is_one_landing_page && <WhatYouBringSection job={job} />}

            {uid && (
              <NotInterestedSignUpSection
                companyColor={COMPANY_COLOR}
                onInterestedClick={openContactFormModal}
              />
            )}

            <NiceToHaveSection job={job} />

            {!job.is_one_landing_page && <YourTeamSection job={job} />}

            <BenefitsSection
              job={job}
              company={company}
              companyColor={COMPANY_COLOR}
            />

            <AboutCompanySection
              job={job}
              company={company}
              showImageCarousel={showImageCarousel}
              setShowImageCarousel={setShowImageCarousel}
            />

            {!job.is_one_landing_page && (
              <>
                <StepsSection
                  companyColor={COMPANY_COLOR}
                  textColor={TEXT_COLOR}
                  isButtonDisabled={ctaButtonsDisabled}
                  onNextStepClick={handleOnNextStepsClick}
                />

                <ReadyStepSection
                  companyColor={COMPANY_COLOR}
                  textColor={TEXT_COLOR}
                  isButtonDisabled={ctaButtonsDisabled}
                  onFunnelOpen={openFunnel}
                />
              </>
            )}
            {uid ? (
              <NotInterestedSignUpSection
                companyColor={COMPANY_COLOR}
                onInterestedClick={openContactFormModal}
              />
            ) : (
              <NotForYouSection
                company={company}
                companyColor={COMPANY_COLOR}
                isJobClosed={isJobClosed}
                onJobShare={shareJob}
              />
            )}

            <Footer lang={lang} />
          </div>
        </div>
        <div
          className={cx(styles.bottomCTA, {
            [styles.hidden]:
              !showFixedBottomCTA || showFunnelModal || showImageCarousel,
          })}
        >
          <Transition
            show={showFixedBottomCTA}
            enter={styles.enter}
            enterFrom={styles.enterFrom}
            enterTo={styles.enterTo}
            leave={styles.leave}
            leaveFrom={styles.leaveFrom}
            leaveTo={styles.leaveTo}
            className={styles.transition}
          >
            <div
              className={styles.bottomCTAContent}
              style={{
                backgroundColor: COMPANY_COLOR,
                color: TEXT_COLOR,
              }}
            >
              <div className={styles.tabletCTA}>
                <span className={styles.title}>
                  {jobTitle}
                  {job.extra_info?.can_be_disclosed
                    ? ` @ ${company.company_name}`
                    : ''}
                </span>
                <span
                  className={styles.buttonContainer}
                  data-tooltip-id="cta-disabled"
                >
                  <Button
                    data-testid="interested-btn-tablet-bottom"
                    className="interested-btn"
                    style={{
                      color: TEXT_COLOR === '#fff' ? COMPANY_COLOR : TEXT_COLOR,
                      width: 200,
                    }}
                    type="default"
                    disabled={ctaButtonsDisabled}
                    onClick={openFunnel}
                  >
                    {t(
                      `description_page.main_section.cta_btn_interested.${approach}`,
                    )}
                  </Button>
                  <GdprMessage className={styles.gdpr} />
                </span>
              </div>
              <div className={styles.mobileCTA} data-tooltip-id="cta-disabled">
                <Button
                  className="interested-btn"
                  style={{
                    color: TEXT_COLOR === '#fff' ? COMPANY_COLOR : TEXT_COLOR,
                  }}
                  type="default"
                  disabled={ctaButtonsDisabled}
                  onClick={openFunnel}
                >
                  {t(
                    `description_page.main_section.cta_btn_interested.${approach}`,
                  )}
                </Button>
                <GdprMessage
                  className={styles.gdpr}
                  linkClassName={styles.gdprLink}
                />
              </div>
            </div>
          </Transition>
        </div>
        <div
          className={cx(styles.notification, {
            [styles.hidden]: !showCopiedToClipboard,
          })}
        >
          <Notification
            show={showCopiedToClipboard}
            title="Link copied to clipboard"
            onClose={() => setShowCopiedToClipboard(false)}
          />
        </div>

        {isJobClosed && (
          <ReactTooltip
            id="cta-disabled"
            variant="dark"
            effect="solid"
            className={styles.jobClosed}
          >
            {t('description_page.main_section.btn_disabled_hint')}
          </ReactTooltip>
        )}
      </div>

      <FunnelStore jobTitle={jobTitle} uid={uid} jobLanguage={lang}>
        {showFunnelModal && (
          <Funnel
            show={showFunnelModal}
            company={company}
            job={job}
            jobSlug={slug}
            uid={uid}
            talentId={talentId}
            setTalentId={setTalentId}
            onClose={() => setShowFunnelModal(false)}
          />
        )}
      </FunnelStore>
      {showContactFormModal && (
        <ContactFormModal
          show={showContactFormModal}
          company={company}
          job={job}
          talentId={talentId}
          onClose={() => {
            setShowContactFormModal(false);
          }}
        />
      )}
    </>
  );
};

JobDescription.propTypes = {
  job: PropTypes.shape().isRequired,
  company: PropTypes.shape().isRequired,
  slug: PropTypes.string,
  uid: PropTypes.string,
  salaryCheck: PropTypes.string,
  lang: PropTypes.string.isRequired,
};

export default JobDescription;
